import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useBannerInteraction } from '../../hooks/useBannerInteraction';
import { useBannerStore } from '../../stores';
import { isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../../utils/layerUtils';

const HANDLE_SIZE = 8;

interface BannerCanvasProps {
  maxViewportWidth?: number;
  maxViewportHeight?: number;
}

export function BannerCanvas({
  maxViewportWidth = 900,
  maxViewportHeight = 500,
}: BannerCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loadedImages, setLoadedImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [loadedLayers, setLoadedLayers] = useState<
    Map<string, Map<string, HTMLImageElement>>
  >(new Map());
  const [backgroundImg, setBackgroundImg] = useState<HTMLImageElement | null>(null);
  const [displayScale, setDisplayScale] = useState(1);

  const {
    canvasWidth,
    canvasHeight,
    backgroundImage,
    backgroundColor,
    elements,
    selectedElementId,
    selectElement,
    updateElementPosition,
    updateElementScale,
    bringToFront,
    setDragging,
    setResizing,
  } = useBannerStore();

  // Track the actual rendered size of the canvas to calculate interaction scale
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateScale = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width > 0 && canvasWidth > 0) {
        setDisplayScale(rect.width / canvasWidth);
      }
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(canvas);

    return () => observer.disconnect();
  }, [canvasWidth, canvasHeight]);

  const {
    isDragging,
    isResizing,
    getElementBounds,
  } = useBannerInteraction({
    canvasRef,
    elements,
    selectedElementId,
    canvasWidth,
    canvasHeight,
    displayScale,
    onSelectElement: selectElement,
    onUpdatePosition: updateElementPosition,
    onUpdateScale: updateElementScale,
    onBringToFront: bringToFront,
    onDragStart: () => setDragging(true),
    onDragEnd: () => setDragging(false),
    onResizeStart: (handle) => setResizing(true, handle),
    onResizeEnd: () => setResizing(false),
    loadedImages,
  });

  // Persistent URL-keyed image cache. Survives re-renders and reload cycles so a
  // layer URI fetched once is never fetched again while this canvas is mounted.
  const imageCacheRef = useRef(new Map<string, HTMLImageElement>());

  const loadImage = useCallback((url: string): Promise<HTMLImageElement> => {
    const cached = imageCacheRef.current.get(url);
    if (cached) return Promise.resolve(cached);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imageCacheRef.current.set(url, img);
        resolve(img);
      };
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }, []);

  useEffect(() => {
    if (!backgroundImage) {
      setBackgroundImg(null);
      return;
    }

    loadImage(backgroundImage)
      .then(setBackgroundImg)
      .catch(() => setBackgroundImg(null));
  }, [backgroundImage, loadImage]);

  // Stable identity key for the NFT/layer sources on the canvas. Position, scale,
  // rotation and zIndex changes (every drag frame) intentionally do NOT change this
  // key, so dragging never triggers a reload cycle.
  const nftSourceKey = useMemo(
    () =>
      elements
        .map(
          (e) =>
            `${e.id}:${e.nftData.mintAddress}:${e.nftData.imageUrl}:${e.nftData.layerAssets
              .map((l) => `${l.id}=${l.uri}`)
              .join(',')}`
        )
        .join('|'),
    [elements]
  );

  useEffect(() => {
    // Guard against out-of-order resolution: a slower earlier run must never
    // overwrite the maps of a later run (previously dropped freshly loaded layers).
    let cancelled = false;

    const loadAllImages = async () => {
      const newImages = new Map<string, HTMLImageElement>();
      const newLayers = new Map<string, Map<string, HTMLImageElement>>();

      for (const element of elements) {
        if (cancelled) return;
        const mintAddress = element.nftData.mintAddress;

        if (!newImages.has(mintAddress)) {
          try {
            const img = await loadImage(element.nftData.imageUrl);
            if (cancelled) return;
            newImages.set(mintAddress, img);
          } catch (err) {
            console.error('Failed to load NFT image:', err);
          }
        }

        if (element.nftData.layerAssets.length > 0) {
          const layerImages = new Map<string, HTMLImageElement>();
          for (const layer of element.nftData.layerAssets) {
            if (cancelled) return;
            try {
              const layerImg = await loadImage(layer.uri);
              if (cancelled) return;
              layerImages.set(layer.id, layerImg);
            } catch (err) {
              console.error('Failed to load layer:', layer.id, err);
            }
          }
          newLayers.set(element.id, layerImages);
        }
      }

      if (!cancelled) {
        setLoadedImages(newImages);
        setLoadedLayers(newLayers);
      }
    };

    loadAllImages();

    return () => {
      cancelled = true;
    };
    // Keyed on nftSourceKey (not the element objects) so transform-only updates
    // skip this effect entirely. elements is read from the closure of the
    // render that produced the current key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftSourceKey, loadImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Disable antialiasing for crisp pixel art rendering
    ctx.imageSmoothingEnabled = false;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    if (backgroundImg) {
      const imgAspect = backgroundImg.width / backgroundImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;
      let drawY: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        drawY = (canvasHeight - drawHeight) / 2;
      }

      ctx.drawImage(
        backgroundImg,
        Math.round(drawX),
        Math.round(drawY),
        Math.round(drawWidth),
        Math.round(drawHeight)
      );
    } else {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

    // Helper function to draw full-canvas layer with vertical offset
    const drawFullCanvasLayer = (
      layerImg: HTMLImageElement,
      verticalOffset: number = 0
    ) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
      }

      // Apply vertical offset as percentage of overflow
      const verticalOverflow = drawHeight - canvasHeight;
      const yOffset = (verticalOffset / 100) * (verticalOverflow / 2);
      const drawY = (canvasHeight - drawHeight) / 2 + yOffset;

      ctx.drawImage(
        layerImg,
        Math.round(drawX),
        Math.round(drawY),
        Math.round(drawWidth),
        Math.round(drawHeight)
      );
    };

    const shouldRenderNFTBackgrounds = !backgroundImage;

    // PASS 1: Draw ALL background layers first (in z-order)
    if (shouldRenderNFTBackgrounds) {
      for (const element of sortedElements) {
        const elementLayers = loadedLayers.get(element.id);
        if (!elementLayers || elementLayers.size === 0) continue;
        if (!element.useBackgroundLayer) continue;

        const backgroundLayers = element.nftData.layerAssets.filter(isBackgroundLayer);

        for (const layer of backgroundLayers) {
          const layerImg = elementLayers.get(layer.id);
          if (layerImg) {
            drawFullCanvasLayer(layerImg, element.backgroundVerticalOffset || 0);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers (in z-order)
    for (const element of sortedElements) {
      ctx.save();

      const elementLayers = loadedLayers.get(element.id);
      const hasLayers = elementLayers && elementLayers.size > 0;

      // Round translation to eliminate sub-pixel jitter
      const x = Math.round(element.position.x);
      const y = Math.round(element.position.y);

      ctx.translate(x, y);
      ctx.rotate((element.rotation * Math.PI) / 180);

      if (hasLayers && elementLayers) {
        const characterLayers = element.nftData.layerAssets.filter((layer) => {
          if (element.selectedLayers.length > 0) {
            return element.selectedLayers.includes(layer.id);
          }

          // Background/frame handled in pass 1 / pass 3
          return isCharacterLayer(layer);
        });

        const refImage = loadedImages.get(element.nftData.mintAddress);
        const refWidth = refImage ? refImage.width : 512;
        const refHeight = refImage ? refImage.height : 512;

        const sw = Math.round(refWidth * element.scale);
        const sh = Math.round(refHeight * element.scale);
        const dx = Math.round(-sw / 2);
        const dy = Math.round(-sh / 2);

        for (const layer of characterLayers) {
          const layerImg = elementLayers.get(layer.id);
          if (layerImg) {
            ctx.drawImage(layerImg, dx, dy, sw, sh);
          }
        }
      } else {
        // Fallback: draw full NFT image at position and scale
        const image = loadedImages.get(element.nftData.mintAddress);
        if (image) {
          const sw = Math.round(image.width * element.scale);
          const sh = Math.round(image.height * element.scale);
          const dx = Math.round(-sw / 2);
          const dy = Math.round(-sh / 2);
          ctx.drawImage(image, dx, dy, sw, sh);
        }
      }

      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last (in z-order)
    for (const element of sortedElements) {
      const elementLayers = loadedLayers.get(element.id);
      if (!elementLayers || elementLayers.size === 0) continue;
      if (!element.useFrameLayer) continue;

      const frameLayers = element.nftData.layerAssets.filter(isFrameLayer);

      for (const layer of frameLayers) {
        const layerImg = elementLayers.get(layer.id);
        if (layerImg) {
          drawFullCanvasLayer(layerImg, element.frameVerticalOffset || 0);
        }
      }
    }

    const selectedElement = elements.find((e) => e.id === selectedElementId);
    if (selectedElement) {
      const bounds = getElementBounds(selectedElement);
      if (bounds) {
        ctx.save();

        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        // Stroke rect with rounded values
        ctx.strokeRect(
          Math.round(bounds.left),
          Math.round(bounds.top),
          Math.round(bounds.width),
          Math.round(bounds.height)
        );
        ctx.setLineDash([]);

        ctx.fillStyle = '#3b82f6';
        const centerX = Math.round(selectedElement.position.x);
        const centerY = Math.round(selectedElement.position.y);

        const handles = [
          { x: bounds.left, y: bounds.top },
          { x: centerX, y: bounds.top },
          { x: bounds.right, y: bounds.top },
          { x: bounds.right, y: centerY },
          { x: bounds.right, y: bounds.bottom },
          { x: centerX, y: bounds.bottom },
          { x: bounds.left, y: bounds.bottom },
          { x: bounds.left, y: centerY },
        ];

        for (const handle of handles) {
          ctx.fillRect(
            Math.round(handle.x - HANDLE_SIZE / 2),
            Math.round(handle.y - HANDLE_SIZE / 2),
            HANDLE_SIZE,
            HANDLE_SIZE
          );
        }

        ctx.restore();
      }
    }
  }, [
    canvasWidth,
    canvasHeight,
    backgroundImg,
    backgroundImage,
    backgroundColor,
    elements,
    selectedElementId,
    loadedImages,
    loadedLayers,
    getElementBounds,
  ]);

  return (
    <div className="flex items-center justify-center bg-surface-secondary dark:bg-neutral-800/50 rounded-xl p-2 sm:p-4 w-full overflow-hidden">
      <div
        style={{
          aspectRatio: `${canvasWidth} / ${canvasHeight}`,
          width: '100%',
          maxWidth: maxViewportWidth,
          maxHeight: maxViewportHeight,
        }}
        className="relative shadow-lg border border-surface-border dark:border-neutral-700 rounded-lg overflow-hidden bg-black"
      >
        <canvas
          ref={canvasRef}
          dir="ltr"
          width={canvasWidth}
          height={canvasHeight}
          className="w-full h-full block"
          style={{
            cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'default',
            imageRendering: 'pixelated',
            direction: 'ltr',
            unicodeBidi: 'bidi-override',
          }}
        />
      </div>
    </div>
  );
}
